import { OverlayGlass } from '../../components/Glass';
import { OVERLAY_FILL_CLASS_ANDROID } from '../../components/Tooltip/surface';
import { FileUp } from '../../icons';
import { Text } from '../../primitives/Text';
import { View } from '../../primitives/View';
import { brandColor } from '../../styles/brand-colors';
import { useTheme } from '../../theme';
import { cn } from '../../utils/cn';

/**
 * THE drop-target look, in one place: the dashed primary frame the quiz
 * buckets already used (QuizAnswerBoard), now worn by every surface that
 * accepts a drop — web upload zones, Home, Ka chat, flashcard images, and the
 * app's in-app media targets. Tokens only, so it flips with dark mode.
 */
export const DROP_TARGET_ACTIVE_CLASS =
  'border border-dashed border-primary-500 bg-primary-50';
export const DROP_TARGET_IDLE_CLASS =
  'border border-dashed border-secondary-300 bg-secondary-100';
/**
 * The overlay's own fill: the same dashed frame over frosted glass rather than
 * a flat `primary-50` slab — laid over a whole page (Home, Ka chat) the solid
 * tint read as harsh. Native mirrors the blur with `OverlayGlass`.
 */
export const DROP_OVERLAY_GLASS_CLASS =
  'border border-dashed border-primary-500 bg-secondary-100/70 web:backdrop-blur-md';

export type DropOverlayProps = {
  /** Pre-translated ("Drop files here"). Ignored when `compact`. */
  title?: string;
  description?: string;
  /** The frame alone, for a target too small to carry a label (the Ka tile). */
  compact?: boolean;
  /** The host's radius and z-index — each surface has its own. */
  className?: string;
  testID?: string;
};

/**
 * The armed state of a drop target, laid OVER its host (the host is
 * `relative`). Visual only: the drag events are each platform's — this never
 * takes a pointer, so drops still reach the host and anything nested in it.
 * It mounts and unmounts rather than fading, which also honours reduced motion.
 * Hidden from assistive tech: a drop is never the only way to add a file.
 */
export function DropOverlay({
  title,
  description,
  compact,
  className,
  testID,
}: DropOverlayProps) {
  const { isDark } = useTheme();
  return (
    <View
      className={cn(
        'absolute inset-0 z-20 items-center justify-center gap-2',
        // The frame alone on a small target: its own face (Ka) stays visible.
        compact
          ? [DROP_TARGET_ACTIVE_CLASS, 'bg-transparent p-0']
          : [DROP_OVERLAY_GLASS_CLASS, 'overflow-hidden p-6'],
        className,
      )}
      testID={testID}
      webProps={{ 'aria-hidden': true, style: { pointerEvents: 'none' } }}
      nativeProps={{
        pointerEvents: 'none',
        importantForAccessibility: 'no-hide-descendants',
      }}>
      {compact ? null : (
        <>
          {/* Web blurs via `backdrop-blur` above; this is the native frost.
              Inside the app's blur target Android can't frost — it paints the
              opaque-enough fill instead. */}
          <OverlayGlass fallbackClassName={OVERLAY_FILL_CLASS_ANDROID} />
          <FileUp size={40} color={brandColor('primary', '500', isDark)} />
          {/* `unicode-bidi: plaintext` — the chat shell forces `direction: ltr`
              (index.css `.ltr-content`), so Hebrew needs the text itself to
              pick its direction. Each Text carries its own ramp: native has
              no inheritance through a View. */}
          {title ? (
            <Text className="text-center text-h4 font-semibold text-primary-900 native:font-mona-semibold web:[unicode-bidi:plaintext]">
              {title}
            </Text>
          ) : null}
          {description ? (
            <Text className="max-w-xs text-center text-b2 font-medium text-body-600 native:font-mona-medium web:[unicode-bidi:plaintext]">
              {description}
            </Text>
          ) : null}
        </>
      )}
    </View>
  );
}
(DropOverlay as { displayName?: string }).displayName = 'DropOverlay';
